import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { haptics } from "@/utils/haptics";
import { PrefetchLink } from "@/components/PrefetchLink";
import { Button } from "@/components/ui/button";
import { useIsTablet } from "@/hooks/use-tablet";
import { useTabletSidebar } from "@/contexts/TabletSidebarContext";
import { useEffectiveSegmentDetailed } from "@/hooks/useEffectiveSegment";
import { getNavItemsForSegment } from "@/lib/navigation/segmentNavItems";

export const TabletSidebar = () => {
  const { isCollapsed, toggleSidebar } = useTabletSidebar();
  const location = useLocation();
  const isTablet = useIsTablet();
  const { segment, isReady } = useEffectiveSegmentDetailed();
  const filteredNavItems = getNavItemsForSegment(isReady ? segment : null, { mobile: false });

  const handleNavClick = () => {
    haptics.light();
  };

  const handleToggle = () => {
    haptics.light();
    toggleSidebar();
  };


  // Only render on tablet screens
  if (!isTablet) return null;

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 bottom-0 z-40 bg-card border-r border-border transition-all duration-300 ease-in-out flex flex-col",
        isCollapsed ? "w-16" : "w-56"
      )}
    >
      {/* Header */}
      <div className={cn(
        "h-14 flex items-center border-b border-border px-3",
        isCollapsed ? "justify-center" : "justify-between"
      )}>
        {!isCollapsed && (
          <span className="font-semibold text-foreground truncate">Career Path</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleToggle}
          className="h-9 w-9 flex-shrink-0"
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {filteredNavItems.map((item) => {
            const isActive = item.path === "/"
              ? location.pathname === "/"
              : location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            const Icon = item.icon;

            return (
              <li key={item.path}>
                <PrefetchLink
                  to={item.path}
                  onClick={handleNavClick}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    "hover:bg-muted active:scale-[0.98]",
                    isActive 
                      ? "bg-primary/10 text-primary font-medium" 
                      : "text-muted-foreground hover:text-foreground",
                    isCollapsed && "justify-center px-2"
                  )}
                  title={isCollapsed ? item.label : undefined}
                >
                  <Icon className={cn(
                    "flex-shrink-0 transition-transform",
                    "w-5 h-5",
                    isActive && "scale-110"
                  )} />
                  {!isCollapsed && (
                    <span className="truncate">{item.label}</span>
                  )}
                </PrefetchLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className={cn(
        "border-t border-border py-3 px-2",
        isCollapsed ? "text-center" : ""
      )}>
        {!isCollapsed && (
          <p className="text-xs text-muted-foreground px-3">
            © 2024 Career Path
          </p>
        )}
      </div>
    </aside>
  );
};
